import React, { useState, useEffect } from 'react';
import { Plus, Minus, Search, Edit, X } from 'lucide-react';
import Loading from "../../component/Loading/Loading";
import AlertMsg from '../../component/AlertMsg/AlertMsg';
import { useSelector } from 'react-redux';
import Pagination from '../../utils/Pagenation/Pagenation';
import useAxiosInstance from '../../services/useAxiosInstance';
import GlassmorphicModal from "../../component/ReusableComponents/GlassmorphicModal";

const FAQS = () => {
  const axiosInstance = useAxiosInstance();
  const communityId = useSelector((state) => state.auth.communityId);
  const userId = useSelector((state) => state.auth.userId);
  const roleIds = useSelector((state) => state.auth.roleId);

  const [searchTerm, setSearchTerm] = useState("");
  const [faqsData, setFaqsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ visible: false, message: '', type: '' });
  const [errors, setErrors] = useState({});
  const [filterRoleId, setFilterRoleId] = useState(roleIds);
  const [showfaqForm, setShowFaqForm] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  const initialFormData = {
    activestatus: true,
    answer: '',
    communityid: communityId,
    createdbyid: userId,
    createddate: new Date().toISOString(),
    faqid: 0,
    modifiedbyid: userId,
    modifieddate: new Date().toISOString(),
    question: '',
    roleid: null
  };

  const [formData, setFormData] = useState(initialFormData);

  const fetchFaqsByRoleId = async (roleId) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/public/faq/getByRoleId/{roleId}?roleId=${filterRoleId}`);
      setFaqsData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filterRoleId === 1000) {
      fetchFaqsByRoleId(0);
    } else {
      fetchFaqsByRoleId(filterRoleId);
    }
  }, [filterRoleId]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axiosInstance.get(`/api/public/role/getRoles`);
        setRoles(response.data);
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
    };

    fetchRoles();
  }, []);

  const handleRoleFilterChange = (event) => {
    const { value } = event.target;
    setFilterRoleId(value);
  };

  const toggleFAQ = (index) => {
    setOpenIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleRoleChange = (event) => {
    const { value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      roleid: value
    }));
    setSelectedRole(value);
  };

  const validateFormData = (data) => {
    const errors = {};
    if (!data.answer.trim()) {
      errors.answer = "Answer is required";
    }
    if (!data.question.trim()) {
      errors.question = "Question is required";
    }
    if (!data.roleid) {
      errors.roleid = "Role is required";
    }
    return errors;
  };

  const handleEditIconClick = (faqId) => {
    setLoading(true);
    // Reset form first to clear any previous data
    setFormData(initialFormData);

    // Make API call to fetch the FAQ data
    axiosInstance.get(`/api/public/faq/getOne/${faqId}`)
      .then((response) => {
        const apiData = response.data;
        // Transform API data to match your form structure
        setFormData({
          activestatus: apiData.activestatus,
          answer: apiData.answer,
          communityid: apiData.communityid,
          createdbyid: apiData.createdbyid,
          createddate: apiData.createddate,
          faqid: apiData.faqid,
          modifiedbyid: userId,
          modifieddate: new Date().toISOString(),
          question: apiData.question,
          roleid: apiData.roleid
        });
        setShowFaqForm(true);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching FAQ data:", error);
        setAlert({
          visible: true,
          message: 'Error loading FAQ data. Please try again',
          type: 'error'
        });
        setLoading(false);
      });
  };

  const handleSubmit = async () => {
    const validationErrors = validateFormData(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setShowFaqForm(false);
      setLoading(true);

      if (formData.faqid === 0) {
        const response = await axiosInstance.post(`/api/public/faq/add`, formData);
        setAlert({ visible: true, message: 'FAQ added successfully!', type: 'success' });

        // Refresh the FAQ list after adding
        if (filterRoleId === 1000) {
          await fetchFaqsByRoleId(0);
        } else {
          await fetchFaqsByRoleId(filterRoleId);
        }
      } else {
        const response = await axiosInstance.put(`/api/public/faq/update/${formData.faqid}`, formData, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        setAlert({ visible: true, message: 'FAQ updated successfully!', type: 'success' });

        // Refresh the FAQ list after updating
        if (filterRoleId === 1000) {
          await fetchFaqsByRoleId(0);
        } else {
          await fetchFaqsByRoleId(filterRoleId);
        }
      }

      // Reset form
      setFormData(initialFormData);

    } catch (error) {
      setAlert({ visible: true, message: 'Error processing request: Please try again.', type: 'error' });
      console.error('Error processing request:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddfaqClick = () => {
    // Reset form to initial empty state
    setFormData(initialFormData);
    setErrors({});
    setShowFaqForm(true);  // Show the form
  };

  const handleCancel = () => {
    setErrors({});
    setShowFaqForm(false);  // Hide the form
  };

  const filteredFaqsData = faqsData.filter((faqsData) =>
    faqsData.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faqsData.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pageCount = Math.ceil(filteredFaqsData.length / itemsPerPage);

  const filteredData = filteredFaqsData.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  // Modal footer with action buttons
  const faqModalFooter = (
    <div className="flex justify-end space-x-2">
      <button
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        onClick={handleCancel}
      >
        Cancel
      </button>
      <button
        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        onClick={handleSubmit}
      >
        {formData.faqid === 0 ? 'Add FAQ' : 'Update FAQ'}
      </button>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen md:p-4">
      {loading && <Loading />}
      {!loading && (
        <div className="max-w-7xl mx-auto">
          {alert.visible && (
            <AlertMsg
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert({ ...alert, visible: false })}
            />
          )}

          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">FAQs</h1>
            {roleIds === 1000 && (
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2"
                onClick={handleAddfaqClick}
              >
                <Plus size={18} />
                <span>Add FAQ</span>
              </button>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            {roleIds === 1000 ? (
              <>
                <div className="p-4 flex flex-col md:flex-row items-start gap-2 md:gap-4">
                  <div className="relative w-full md:w-80 md:mb-0">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Search FAQs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="w-full md:w-auto">
                    <select
                      id="roleFilter"
                      value={filterRoleId}
                      onChange={handleRoleFilterChange}
                      className="px-3 py-[11px] rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                    >
                      <option value="0">Select a role</option>
                      {roles.map((role) => (
                        <option key={role.roleid} value={role.roleid}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-blue-400">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Question</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Answer</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredData.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">No Data Found</td>
                        </tr>
                      ) : (
                        filteredData.map((item) => (
                          <tr key={item.faqid} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm text-gray-900">{item.question}</td>
                            <td className="px-6 py-4 text-sm text-gray-900 max-w-[500px] whitespace-wrap">{item.answer}</td>
                            <td className="px-6 py-4  text-sm text-gray-900">
                              {roles.find(role => role.roleid === item.roleid)?.name}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium">
                              <button
                                onClick={() => handleEditIconClick(item.faqid)}
                                className="text-gray-500 hover:text-gray-700"
                                title="Edit FAQ"
                              >
                                <Edit size={18} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
                  <div className="text-sm text-gray-700">
                    Page {currentPage + 1} of {pageCount}
                  </div>
                  <div className="flex-1 flex justify-end">
                    <Pagination
                      currentPage={currentPage}
                      pageCount={pageCount}
                      setCurrentPage={setCurrentPage}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredData.length === 0 ? (
                  <div className="px-6 py-4 text-center text-sm text-gray-500">No FAQs Found</div>
                ) : (
                  filteredData.map((faq, index) => (
                    <div key={index} className="border-b">
                      <div
                        className="px-6 py-4 flex justify-between items-center cursor-pointer"
                        onClick={() => toggleFAQ(index)}
                      >
                        <h3 className="text-lg font-medium text-gray-900">{faq.question}</h3>
                        <button
                          className={`rounded-full p-1 focus:outline-none transition-colors ${openIndex === index
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-blue-100'
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFAQ(index);
                          }}
                        >
                          {openIndex === index ? (
                            <Minus size={20} />
                          ) : (
                            <Plus size={20} />
                          )}
                        </button>
                      </div>

                      {openIndex === index && (
                        <div className="px-6 py-4 bg-gray-50">
                          <p className="text-gray-700">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* FAQ Form Modal */}
          <GlassmorphicModal
            isOpen={showfaqForm}
            onClose={handleCancel}
            title={formData.faqid === 0 ? 'Add FAQ' : 'Edit FAQ'}
            size="lg"
            footer={faqModalFooter}
          >
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  FAQ Question <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  maxLength={100}
                  name="question"
                  value={formData.question}
                  onChange={handleChange}
                  placeholder="Enter your question"
                  className={`w-full rounded-md border ${errors.question ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {errors.question && <p className="text-red-500 text-xs mt-1">{errors.question}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  FAQ Answer <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="answer"
                  maxLength={200}
                  value={formData.answer}
                  onChange={handleChange}
                  placeholder="Enter your answer"
                  rows="4"
                  className={`w-full rounded-md border ${errors.answer ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                ></textarea>
                {errors.answer && <p className="text-red-500 text-xs mt-1">{errors.answer}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  id="role-select"
                  value={formData.roleid || ''}
                  onChange={handleRoleChange}
                  className={`w-full rounded-md border ${errors.roleid ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="">Select a role</option>
                  {roles.map((role) => (
                    <option key={role.roleid} value={role.roleid}>
                      {role.name}
                    </option>
                  ))}
                </select>
                {errors.roleid && <p className="text-red-500 text-xs mt-1">{errors.roleid}</p>}
              </div>

              <div className="text-sm text-red-500">
                * Required fields
              </div>
            </div>
          </GlassmorphicModal>
        </div>
      )}
    </div>
  );
};

export default FAQS;